import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowClockwise,
  CircleNotch,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  ArrowsOutSimple,
  ArrowsInSimple,
  CaretLeft,
  CaretRight,
  WarningCircle,
  DownloadSimple,
  CircleHalf,
  ArrowSquareOut,
  Printer,
  Sparkle,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3.0;
const DEFAULT_ZOOM = 1.0;

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

interface CompileErrorInfo {
  line: number | null;
  message: string;
  severity: string;
  file: string | null;
}

interface PdfPreviewProps {
  pdfUrl: string | null;
  isCompiling?: boolean;
  isAutoCompiling?: boolean;
  autoCompile?: boolean;
  compileErrors?: CompileErrorInfo[];
  onToggleAutoCompile?: () => void;
  onCompile?: () => void;
  onShowErrors?: () => void;
  onAIFix?: () => void;
  className?: string;
}

function ToolbarBtn({
  onClick,
  disabled,
  label,
  className: cx,
  children,
}: {
  onClick?: () => void;
  disabled?: boolean;
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      whileHover={disabled ? {} : { scale: 1.08 }}
      whileTap={disabled ? {} : { scale: 0.92 }}
      transition={spring}
      aria-label={label}
      title={label}
      className={cn(
        "flex items-center justify-center rounded-[var(--radius-sm)] p-1.5",
        "transition-colors duration-75",
        disabled
          ? "text-[var(--text-muted)] opacity-30 cursor-not-allowed"
          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
        cx,
      )}
    >
      {children}
    </motion.button>
  );
}

export function PdfPreview({
  pdfUrl,
  isCompiling = false,
  isAutoCompiling = false,
  autoCompile = true,
  compileErrors = [],
  onToggleAutoCompile,
  onCompile,
  onShowErrors,
  onAIFix,
  className,
}: PdfPreviewProps) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderedPages, setRenderedPages] = useState<HTMLCanvasElement[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [inverted, setInverted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const previewRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const loadTaskRef = useRef<ReturnType<typeof pdfjsLib.getDocument> | null>(null);

  const totalPages = pdf?.numPages ?? 0;
  const hasPages = renderedPages.length > 0;
  const hasCompileErrors = compileErrors.length > 0 && !pdfUrl;
  const showEmpty = !pdfUrl && !isLoading && !isCompiling && !hasCompileErrors;
  const showSpinner = (isLoading || isCompiling) && !hasPages;

  useEffect(() => {
    if (!pdfUrl) {
      setPdf(null);
      setRenderedPages([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setRenderedPages([]);

    if (loadTaskRef.current) {
      try { loadTaskRef.current.destroy(); } catch { /* noop */ }
    }

    const loadingTask = pdfjsLib.getDocument(pdfUrl);
    loadTaskRef.current = loadingTask;

    loadingTask.promise
      .then((doc) => {
        if (!cancelled) setPdf(doc);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        const isCancellation =
          (err instanceof Error && err.name === "RenderingCancelledException") ||
          msg.includes("Worker was destroyed") ||
          msg.includes("Loading aborted");
        if (isCancellation) return;
        setError(msg || "Failed to load PDF");
        setPdf(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (loadTaskRef.current) {
        try { loadTaskRef.current.destroy(); } catch { /* noop */ }
        loadTaskRef.current = null;
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;

    async function renderAllPages(doc: PDFDocumentProxy, scale: number) {
      const dpr = window.devicePixelRatio || 1;
      const canvases: HTMLCanvasElement[] = [];

      try {
        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const hiResViewport = page.getViewport({ scale: scale * dpr });
          const cssViewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;

          canvas.width = Math.floor(hiResViewport.width);
          canvas.height = Math.floor(hiResViewport.height);
          canvas.style.width = `${Math.floor(cssViewport.width)}px`;
          canvas.style.height = `${Math.floor(cssViewport.height)}px`;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await page.render({ canvasContext: ctx, viewport: hiResViewport, canvas } as any).promise;
          canvases.push(canvas);
        }
        if (!cancelled) setRenderedPages(canvases);
      } catch {
        // PDF was destroyed while rendering (e.g. document changed) — ignore
      }
    }

    renderAllPages(pdf, zoom);
    return () => { cancelled = true; };
  }, [pdf, zoom]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || renderedPages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let bestPage = currentPage;
        let bestRatio = 0;
        for (const entry of entries) {
          const pageIdx = Number(entry.target.getAttribute("data-page"));
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestPage = pageIdx;
          }
        }
        if (bestRatio > 0) setCurrentPage(bestPage);
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    pageRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [renderedPages, currentPage]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!previewRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      previewRef.current.requestFullscreen().catch(() => {});
    }
  }, []);

  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN)), []);
  const zoomFit = useCallback(() => setZoom(DEFAULT_ZOOM), []);

  const goToPage = useCallback((page: number) => {
    const clamped = Math.max(1, Math.min(page, totalPages));
    const el = pageRefs.current.get(clamped);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setCurrentPage(clamped);
    }
  }, [totalPages]);

  const prevPage = useCallback(() => goToPage(currentPage - 1), [goToPage, currentPage]);
  const nextPage = useCallback(() => goToPage(currentPage + 1), [goToPage, currentPage]);

  const padNum = (n: number) => String(n).padStart(2, "0");

  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [pdfUrl]);

  const handleOpenInNewTab = useCallback(() => {
    if (!pdfUrl) return;
    window.open(pdfUrl, "_blank");
  }, [pdfUrl]);

  const handlePrint = useCallback(() => {
    if (!pdfUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  }, [pdfUrl]);

  return (
    <div ref={previewRef} className={cn("flex flex-col h-full overflow-hidden bg-[var(--bg-primary)]", className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-2 py-1 shrink-0 border-b border-[var(--border-hairline)] bg-[var(--bg-surface)]">
        {/* Left: compile + page navigation */}
        <div className="flex items-center gap-1">
          {onCompile && (
            <>
              <motion.button
                onClick={onCompile}
                disabled={isCompiling}
                whileHover={isCompiling ? {} : { scale: 1.04 }}
                whileTap={isCompiling ? {} : { scale: 0.95 }}
                transition={spring}
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold",
                  "transition-colors duration-100",
                  isCompiling
                    ? "bg-[var(--bg-surface-hover)] text-[var(--text-muted)] cursor-wait"
                    : "bg-[var(--color-lever-orange)] text-white hover:brightness-110",
                )}
              >
                <motion.div
                  animate={isCompiling ? { rotate: 360 } : { rotate: 0 }}
                  transition={isCompiling ? { duration: 0.8, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                >
                  <ArrowClockwise size={12} weight="bold" />
                </motion.div>
                {isCompiling ? "Compiling" : "Compile"}
              </motion.button>
              <div className="mx-0.5 h-3 w-px bg-[var(--border-hairline)]" />
            </>
          )}

          <ToolbarBtn onClick={prevPage} disabled={!hasPages || currentPage <= 1} label="Previous page">
            <CaretLeft size={13} weight="bold" />
          </ToolbarBtn>
          {totalPages > 0 ? (
            <span className="text-[11px] font-medium tabular-nums text-[var(--text-secondary)] select-none min-w-[3.5rem] text-center">
              <span className="text-[var(--text-primary)]">{padNum(currentPage)}</span>
              <span className="text-[var(--text-muted)] mx-0.5">/</span>
              <span>{padNum(totalPages)}</span>
            </span>
          ) : (
            <span className="text-[10px] text-[var(--text-muted)] select-none px-1">
              {isCompiling ? "Compiling..." : "No output"}
            </span>
          )}
          <ToolbarBtn onClick={nextPage} disabled={!hasPages || currentPage >= totalPages} label="Next page">
            <CaretRight size={13} weight="bold" />
          </ToolbarBtn>
        </div>

        {/* Right: zoom controls */}
        <div className="flex items-center gap-0.5">
          <ToolbarBtn onClick={zoomOut} disabled={!hasPages || zoom <= ZOOM_MIN} label="Zoom out">
            <MagnifyingGlassMinus size={13} weight="bold" />
          </ToolbarBtn>

          <button
            onClick={zoomFit}
            disabled={!hasPages}
            className={cn(
              "text-[10px] font-medium tabular-nums min-w-[2.5rem] text-center select-none",
              "rounded-[var(--radius-sm)] py-0.5 transition-colors duration-75",
              hasPages
                ? "text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
                : "text-[var(--text-muted)] opacity-30",
            )}
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>

          <ToolbarBtn onClick={zoomIn} disabled={!hasPages || zoom >= ZOOM_MAX} label="Zoom in">
            <MagnifyingGlassPlus size={13} weight="bold" />
          </ToolbarBtn>

          <div className="mx-0.5 h-3 w-px bg-[var(--border-hairline)]" />

          <ToolbarBtn onClick={toggleFullscreen} label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {isFullscreen ? (
              <ArrowsInSimple size={13} weight="bold" />
            ) : (
              <ArrowsOutSimple size={13} weight="bold" />
            )}
          </ToolbarBtn>

          {onToggleAutoCompile && (
            <>
              <div className="mx-0.5 h-3 w-px bg-[var(--border-hairline)]" />
              <button
                onClick={onToggleAutoCompile}
                title={autoCompile ? "Auto-compile on (click to disable)" : "Auto-compile off (click to enable)"}
                className={cn(
                  "flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[10px] font-medium transition-colors duration-75 select-none",
                  autoCompile
                    ? "text-[var(--color-signal-green)] bg-[var(--color-signal-green)]/10"
                    : "text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]",
                )}
              >
                {isAutoCompiling ? (
                  <CircleNotch size={10} weight="bold" className="animate-spin" />
                ) : (
                  <ArrowClockwise size={10} weight="bold" />
                )}
                Auto
              </button>
            </>
          )}

          <div className="mx-0.5 h-3 w-px bg-[var(--border-hairline)]" />

          <ToolbarBtn
            onClick={() => setInverted((v) => !v)}
            label={inverted ? "Normal colors" : "Invert colors"}
            className={inverted ? "text-[var(--text-primary)] bg-[var(--bg-surface-hover)]" : undefined}
          >
            <CircleHalf size={13} weight={inverted ? "fill" : "bold"} />
          </ToolbarBtn>

          <ToolbarBtn onClick={handleDownload} disabled={!pdfUrl} label="Download PDF">
            <DownloadSimple size={13} weight="bold" />
          </ToolbarBtn>

          <ToolbarBtn onClick={handlePrint} disabled={!pdfUrl} label="Print">
            <Printer size={13} weight="bold" />
          </ToolbarBtn>

          <ToolbarBtn onClick={handleOpenInNewTab} disabled={!pdfUrl} label="Open in new tab">
            <ArrowSquareOut size={13} weight="bold" />
          </ToolbarBtn>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex-1 overflow-hidden">
        {/* Subtle auto-compiling overlay — only when pages already rendered */}
        <AnimatePresence>
          {isAutoCompiling && hasPages && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute top-2 right-2 z-10 flex items-center gap-1.5 rounded-full bg-[var(--bg-surface)]/90 backdrop-blur-[var(--blur-sm)] border border-[var(--border-hairline)] px-2.5 py-1 shadow-[var(--shadow-sm)]"
            >
              <CircleNotch size={10} weight="bold" className="animate-spin text-[var(--text-muted)]" />
              <span className="text-[10px] text-[var(--text-muted)] font-medium">Recompiling...</span>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          ref={scrollContainerRef}
          className="h-full overflow-auto bg-[var(--bg-secondary)]"
          style={inverted ? { filter: "invert(1) hue-rotate(180deg)" } : undefined}
        >
          {/* Empty state */}
          <AnimatePresence mode="wait">
            {showEmpty && (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center justify-center h-full gap-3 px-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface)] border border-[var(--border-hairline)]">
                  <ArrowClockwise size={18} weight="bold" className="text-[var(--text-muted)]" />
                </div>
                <div className="text-center">
                  <p className="text-[12px] font-medium text-[var(--text-secondary)]">
                    No preview yet
                  </p>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    Compile your document to see the PDF output
                  </p>
                </div>
                {onCompile && (
                  <motion.button
                    onClick={onCompile}
                    whileHover={{ scale: 1.03, rotate: 0 }}
                    whileTap={{ scale: 0.96 }}
                    transition={spring}
                    className={cn(
                      "group flex items-center gap-1.5 rounded-[var(--radius-md)] px-3.5 py-2",
                      "text-[11px] font-semibold",
                      "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                      "hover:opacity-90 transition-opacity duration-100",
                    )}
                  >
                    <motion.div whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}>
                      <ArrowClockwise size={13} weight="bold" />
                    </motion.div>
                    Compile
                  </motion.button>
                )}
                <p className="text-[9px] text-[var(--text-muted)] mt-1">
                  or press <kbd className="px-1 py-0.5 rounded-[3px] bg-[var(--bg-surface)] border border-[var(--border-hairline)] text-[var(--text-secondary)] font-mono">⌘ S</kbd>
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Compilation errors */}
          <AnimatePresence mode="wait">
            {hasCompileErrors && !isCompiling && (
              <motion.div
                key="compile-errors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center justify-center h-full gap-3 px-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
                  <WarningCircle size={20} weight="bold" className="text-[var(--color-soft-red)]" />
                </div>
                <div className="text-center">
                  <p className="text-[12px] font-medium text-[var(--text-secondary)]">
                    Compilation failed
                  </p>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    {compileErrors.filter((e) => e.severity === "error").length} error{compileErrors.filter((e) => e.severity === "error").length !== 1 ? "s" : ""} found
                  </p>
                </div>
                <div className="w-full max-w-[320px] mt-1 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden">
                  {compileErrors.filter((e) => e.severity === "error").slice(0, 3).map((err, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 px-3 py-2 border-b border-[var(--border-hairline)] last:border-b-0"
                    >
                      <WarningCircle size={11} weight="fill" className="text-[var(--color-soft-red)] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[11px] text-[var(--text-primary)] leading-snug line-clamp-2">{err.message}</p>
                        {err.line && (
                          <p className="text-[9px] text-[var(--text-muted)] mt-0.5">Line {err.line}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {compileErrors.filter((e) => e.severity === "error").length > 3 && (
                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] text-center">
                      +{compileErrors.filter((e) => e.severity === "error").length - 3} more
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1 max-w-[360px]">
                  {onShowErrors && (
                    <motion.button
                      onClick={onShowErrors}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.96 }}
                      transition={spring}
                      className="flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)] transition-colors duration-100"
                    >
                      View all errors
                    </motion.button>
                  )}
                  {onCompile && (
                    <motion.button
                      onClick={onCompile}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.96 }}
                      transition={spring}
                      className="flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)] transition-colors duration-100"
                    >
                      <ArrowClockwise size={11} weight="bold" />
                      Retry
                    </motion.button>
                  )}
                  {onAIFix && (
                    <motion.button
                      onClick={onAIFix}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.96 }}
                      transition={spring}
                      title="Ask the AI to fix compilation errors (opens chat)"
                      className={cn(
                        "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-semibold",
                        "bg-[var(--color-lever-orange)] text-white hover:brightness-110",
                        "transition-all duration-100",
                      )}
                    >
                      <Sparkle size={11} weight="fill" />
                      AI Fix
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading spinner */}
          <AnimatePresence>
            {showSpinner && (
              <motion.div
                key="spinner"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center h-full gap-2"
              >
                <CircleNotch size={22} weight="bold" className="animate-spin text-[var(--text-muted)]" />
                <p className="text-[10px] text-[var(--text-muted)]">
                  {isCompiling ? "Compiling document..." : "Loading PDF..."}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error state */}
          <AnimatePresence>
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center h-full gap-3 px-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
                  <WarningCircle size={20} weight="bold" className="text-[var(--color-soft-red)]" />
                </div>
                <div className="text-center">
                  <p className="text-[12px] font-medium text-[var(--text-secondary)]">
                    Preview failed
                  </p>
                  <p className="text-[10px] text-[var(--color-soft-red)] mt-0.5 max-w-[280px]">
                    {error}
                  </p>
                </div>
                {onCompile && (
                  <motion.button
                    onClick={() => { setError(null); onCompile(); }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.96 }}
                    transition={spring}
                    className="group flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)] transition-colors duration-100"
                  >
                    <motion.div whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}>
                      <ArrowClockwise size={12} weight="bold" />
                    </motion.div>
                    Try again
                  </motion.button>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Rendered pages */}
          {hasPages && (
            <div className="flex flex-col items-center gap-3 py-4 px-3">
              {renderedPages.map((canvas, idx) => (
                <div
                  key={idx}
                  data-page={idx + 1}
                  ref={(el) => {
                    if (el) pageRefs.current.set(idx + 1, el);
                    else pageRefs.current.delete(idx + 1);
                  }}
                  className="bg-[#fff] rounded-[var(--radius-sm)] overflow-hidden shadow-[var(--shadow-md)]"
                  style={{ lineHeight: 0 }}
                >
                  <PageCanvas canvas={canvas} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Floating page nav pill -- page nav only, no compile */}
        <AnimatePresence>
          {totalPages > 1 && !isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={spring}
              className={cn(
                "absolute bottom-3 right-3 flex items-center gap-0.5",
                "rounded-full bg-[var(--bg-surface)]/90 backdrop-blur-[var(--blur-sm)] border border-[var(--border-subtle)]",
                "shadow-[var(--shadow-lg)] px-1 py-0.5",
              )}
            >
              <ToolbarBtn onClick={prevPage} disabled={currentPage <= 1} label="Previous page" className="rounded-full">
                <CaretLeft size={12} weight="bold" />
              </ToolbarBtn>
              <span className="text-[10px] font-medium tabular-nums text-[var(--text-secondary)] select-none min-w-[2rem] text-center">
                {currentPage}
              </span>
              <ToolbarBtn onClick={nextPage} disabled={currentPage >= totalPages} label="Next page" className="rounded-full">
                <CaretRight size={12} weight="bold" />
              </ToolbarBtn>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function PageCanvas({ canvas }: { canvas: HTMLCanvasElement }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    if (!container.contains(canvas)) {
      container.replaceChildren(canvas);
    }
  }, [canvas]);

  return <div ref={ref} />;
}
